<template>
  <div class="h-screen flex flex-col md:flex-row">
    <!-- Left side -->
    <div class="bg-gray-50 flex flex-col justify-center items-center w-full md:w-1/2 p-10">
      <!-- Login form -->
      <form v-if="isLoginForm" @submit.prevent="handleLogin" class="w-full max-w-md">
        <h2 class="text-center text-lg font-semibold mb-8">
          Êtes-vous déjà client ?
        </h2>
        <label class="block text-sm font-semibold mb-1" for="email">
          Email *
        </label>
        <input v-model="loginData.email" class="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400" id="email" placeholder="Email" required type="email"/>
        <label class="block text-sm font-semibold mb-1" for="password">
          Mot de passe *
        </label>
        <div class="relative">
          <input v-model="loginData.password" :type="passwordFieldType" class="w-full mb-2 px-4 py-2 border border-gray-300 rounded-md text-gray-400 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400" id="password" placeholder="Mot de passe" required/>
          <span @click="togglePasswordVisibility" class="absolute inset-y-0 right-3 flex items-center text-gray-500 cursor-pointer">
            <i :class="passwordIconClass"></i>
          </span>
        </div>
        <a class="block mb-6 text-sm font-semibold underline text-black" href="#" @click.prevent="showResetPasswordForm">
          Mot de passe oublié ?
        </a>
        <button class="w-full bg-blue-900 text-white py-3 rounded-md mb-6 hover:bg-gray-900 transition-colors" type="submit">
          Se connecter
        </button>
        <div class="flex items-center mb-6">
          <hr class="flex-grow border-gray-300"/>
          <span class="mx-3 text-xs font-semibold text-gray-600">
            OU
          </span>
          <hr class="flex-grow border-gray-300"/>
        </div>
        <h3 class="text-center text-lg font-semibold mb-4">
          Nouveau Client ?
        </h3>
        <button class="w-full border border-gray-700 rounded-md py-3 text-sm hover:bg-gray-200 transition-colors" type="button" @click="showRegisterForm">
          Créer mon compte
        </button>
      </form>

      <!-- Reset Password form -->
      <form v-if="isResetPasswordForm" @submit.prevent="handleResetPassword" class="w-full max-w-md">
        <h2 class="text-center text-lg font-semibold mb-8">
          Réinitialiser le mot de passe
        </h2>
        <label class="block text-sm font-semibold mb-1" for="email-reset">
          Email *
        </label>
        <input v-model="resetPasswordData.email" class="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400" id="email-reset" placeholder="Email" required type="email"/>
        <button class="w-full bg-blue-900 text-white py-3 rounded-md mb-6 hover:bg-gray-900 transition-colors" type="submit">
          Envoyer le lien de réinitialisation
        </button>
        <button class="w-full border border-gray-700 rounded-md py-3 text-sm hover:bg-gray-200 transition-colors" type="button" @click="showLoginForm">
          Retour à la connexion
        </button>
      </form>

      <!-- Register form -->
      <form v-else-if="!isLoginForm && !isResetPasswordForm" @submit.prevent="handleRegister" class="w-full max-w-md mx-auto">
        <h2 class="text-center text-lg font-semibold mb-6">
          Créer un compte
        </h2>

        <!-- Nom + Prénom -->
        <div class="flex gap-4 mb-4">
          <div class="flex-1">
            <label class="block text-xs font-semibold mb-1" for="nom">Nom *</label>
            <input v-model="registerData.nom" id="nom" placeholder="Nom" required type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div class="flex-1">
            <label class="block text-xs font-semibold mb-1" for="prenom">Prénom *</label>
            <input v-model="registerData.prenom" id="prenom" placeholder="Prénom" required type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
        </div>

        <!-- Email full width -->
        <div class="mb-4">
          <label class="block text-xs font-semibold mb-1" for="email-register">Email *</label>
          <input v-model="registerData.email" id="email-register" placeholder="Email" required type="email"
            class="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <!-- Mot de passe + Confirmation -->
        <div class="flex gap-4 mb-4">
          <div class="flex-1">
            <label class="block text-xs font-semibold mb-1" for="password-register">Mot de passe *</label>
            <input v-model="registerData.password" id="password-register" placeholder="Mot de passe" required type="password"
              class="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div class="flex-1">
            <label class="block text-xs font-semibold mb-1" for="password-confirm">Confirmer le mot de passe *</label>
            <input v-model="registerData.passwordConfirm" id="password-confirm" placeholder="Confirmer" required type="password"
              class="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
        </div>

        <!-- Téléphone full width -->
        <div class="mb-4">
          <label class="block text-xs font-semibold mb-1" for="tel">Téléphone *</label>
          <input v-model="registerData.telephone" id="tel" placeholder="Téléphone" required type="tel"
            class="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <!-- Date de naissance full width -->
        <div class="mb-4">
          <label class="block text-xs font-semibold mb-1" for="date_naissance">Date de naissance *</label>
          <input v-model="registerData.date_naissance" id="date_naissance" required type="date"
            class="w-full px-3 py-2 border border-gray-300 rounded-md" />
        </div>

        <!-- Genre full width -->
        <div class="mb-4">
          <label class="block text-xs font-semibold mb-1" for="genre">Genre *</label>
          <select v-model="registerData.genre" id="genre" required
            class="w-full px-3 py-2 border border-gray-300 rounded-md">
            <option value="" disabled>Choisissez votre genre</option>
            <option value="Homme">Homme</option>
            <option value="Femme">Femme</option>
          </select>
        </div>

        <!-- Checkbox CGU -->
        <div class="flex items-center mb-6 text-sm">
          <input v-model="registerData.acceptCGU" id="cgu" required type="checkbox" class="mr-2" />
          <label for="cgu" class="font-semibold">J'accepte les CGU</label>
        </div>

        <!-- Bouton -->
        <button class="w-full bg-blue-900 text-white py-3 rounded-md hover:bg-gray-900 transition-colors" type="submit">
          Créer mon compte
        </button>

        <!-- Séparateur et lien connexion -->
        <div class="mt-6 text-center">
          <div class="flex items-center mb-6">
            <hr class="flex-grow border-gray-300" />
            <span class="mx-3 text-xs font-semibold text-gray-600">OU</span>
            <hr class="flex-grow border-gray-300" />
          </div>
          <h3 class="text-center text-lg font-semibold mb-4">Vous avez déjà un compte ?</h3>
          <button class="w-full border border-gray-700 rounded-md py-3 text-sm hover:bg-gray-200 transition-colors"
            type="button" @click="showLoginForm">
            Se connecter
          </button>
        </div>
      </form>


    </div>
    <!-- Right side -->
    <div class="w-full md:w-1/2 flex justify-center items-center">
      <img alt="Abstract beige art with black strokes, smooth and glossy texture" class="object-cover w-full h-full" height="800" src="https://img.freepik.com/free-photo/bride-getting-makeup-done-medium-shot_23-2149860778.jpg?uid=R141327891&ga=GA1.1.223045777.1725963888&semt=ais_hybrid&w=740" width="600"/>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isLoginForm: true,
      isResetPasswordForm: false,
      loginData: {
        email: '',
        password: ''
      },
      resetPasswordData: {
        email: ''
      },
      registerData: {
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        date_naissance: '',
        genre: '',
        password: '',
        passwordConfirm: '',
        acceptCGU: false
      },
      passwordFieldType: 'password'
    };
  },
  computed: {
    passwordIconClass() {
      return this.passwordFieldType === 'password' ? 'fas fa-eye-slash' : 'fas fa-eye';
    }
  },
  methods: {
    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
    showRegisterForm() {
      this.isLoginForm = false;
      this.isResetPasswordForm = false;
    },
    showLoginForm() {
      this.isLoginForm = true;
      this.isResetPasswordForm = false;
    },
    showResetPasswordForm() {
      this.isLoginForm = false;
      this.isResetPasswordForm = true;
    },
    handleLogin() {
      console.log('Login data:', this.loginData);

      fetch('https://b53c7d5edf32.ngrok-free.app/api/clients/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(this.loginData)
      })
        .then(async response => {
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Erreur lors de la connexion.');
          }

          // Stocker le token dans le localStorage
          localStorage.setItem('clientToken', data.token);

          console.log('Connexion réussie, token enregistré :', data.token);
          alert('Connexion réussie !');
         const redirectPath = this.$route.query.redirect || '/userAccount'
        this.$router.push(redirectPath)
          // Redirige ou mets à jour l’état ici si besoin
        })
        .catch(error => {
          console.error('Erreur de connexion :', error);
          alert(error.message);
        });
    },
    handleRegister() {
      if (this.registerData.password !== this.registerData.passwordConfirm) {
        alert("Les mots de passe ne correspondent pas.");
        return;
      }

      // Cloner et nettoyer les données à envoyer (retirer passwordConfirm)
      const payload = { ...this.registerData };
      delete payload.passwordConfirm;

      fetch('https://b53c7d5edf32.ngrok-free.app/api/clients/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
        .then(async response => {
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Erreur lors de l\'enregistrement.');
          }
          return response.json();
        })
        .then(data => {
          alert('Compte créé avec succès !');
          console.log('Réponse serveur :', data);
          // Réinitialiser les champs si nécessaire
          this.registerData = {
            nom: '',
            prenom: '',
            email: '',
            telephone: '',
            adresse: '',
            date_naissance: '',
            genre: '',
            password: '',
            passwordConfirm: '',
            acceptCGU: false
          };
          this.showLoginForm(); // Revenir à la page de connexion
        })
        .catch(error => {
          console.error('Erreur :', error);
          alert(error.message);
        });
    },
    handleResetPassword() {
      console.log('Reset password data:', this.resetPasswordData);

      // Envoyer l'email au backend pour la réinitialisation
      fetch('https://b53c7d5edf32.ngrok-free.app/api/clients/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(this.resetPasswordData)
      })
      .then(async response => {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Erreur lors de la demande de réinitialisation.');
        }

        alert('Un email de réinitialisation a été envoyé à votre adresse.');
        this.showLoginForm(); // Revenir à la page de connexion
      })
      .catch(error => {
        console.error('Erreur de réinitialisation :', error);
        alert(error.message);
      });
    }
  }
};
</script>
  
  
  <style scoped>
  /* Add your custom styles here */
  </style>
  